import * as React from 'react';

import styled from 'styled-components';

import { StandardFormFieldDto } from '@app/models/dtos/form';
import { isHexColour } from '@app/constants/theme';
import { cn } from '@app/shadcn/util/lib';
import { IThemeState, useFormState } from '@app/store/jotai/form';
import { styleTokens } from '@app/views/molecules/theme/theme-shared';
import { inputTextOf } from '@app/views/molecules/theme/theme-root';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    textColor?: string;
}

const ShadCNInput = React.forwardRef<HTMLInputElement, InputProps>(({ className, type, value, ...props }, ref) => {
    const { theme } = useFormState();

    return (
        <input
            style={{
                // The answer is the responder's own words — it wears ink (theme
                // primary, or the theme's own input text), never the accent/action
                // colour. 16px floor, and never larger than the 24px question
                // (DESIGN.md, Type).
                color: inputTextOf(theme)
            }}
            type={type}
            className={cn(`w-full rounded-md border bg-white px-4 py-3 text-base outline-hidden transition duration-150 disabled:cursor-not-allowed disabled:opacity-50 lg:text-lg`, className)}
            ref={ref}
            // Coerce null -> '' so a controlled input never receives a null value.
            value={value === null ? '' : value}
            {...props}
        />
    );
});
ShadCNInput.displayName = 'ShadCNInput';

const AppInput = React.forwardRef<HTMLInputElement, InputProps & { icon?: React.ReactElement }>(({ className, type, icon, ...props }, ref) => {
    return (
        <span className={cn('relative w-full')}>
            {icon && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-black-600 pointer-events-none">
                {icon}
            </span>}
            <input type={type} className={cn(`border-control focus:border-brand-500 rounded border px-3 py-2 text-[12px] disabled:cursor-not-allowed disabled:opacity-80`, className, icon && "pl-10")} ref={ref} {...props} />
        </span>
    );
});

AppInput.displayName = 'AppInput';

const FieldInput = styled(ShadCNInput)<{
    $slide?: StandardFormFieldDto;
    $formTheme?: IThemeState;
}>(({ $formTheme }) => {
    // Theme comes in as a prop; calling useFormState() inside a styled-components
    // interpolation yields an inconsistent hook count under React 19.
    const themeColor = $formTheme?.tertiary;
    const secondaryColor = $formTheme?.secondary;
    const tokens = styleTokens($formTheme?.style);
    return {
        // White field on the page surface gives the input real figure/ground —
        // the bordered box, not a wash, is what reads as "type here".
        background: '#ffffff',
        borderRadius: tokens.fieldRadius,
        // Three-step border affordance: the theme's border colour (tertiary) at
        // REST — every preset keeps it at 3:1 or more on its ground, so the
        // field's edge is visible (WCAG 1.4.11); the theme's ink on HOVER; the
        // action colour on FOCUS, with the global focus outline (tailwind.css,
        // --focus-ring set by the layout wrapper).
        borderColor: themeColor || '#818CA0',
        // The field wrapper passes `style` through as undefined, which replaces the
        // inline colour above: so a theme's own input text is set here as well. A
        // theme without one keeps what these inputs always drew (the page's ink).
        ...(isHexColour($formTheme?.inputText) ? { color: $formTheme!.inputText! } : {}),
        '&::placeholder': {
            // Legible neutral (ink-3), not the theme tint — placeholders are
            // text people read, not decoration.
            color: '#667085 !important'
        },
        '&:hover:not(:focus)': {
            borderColor: $formTheme?.primary || '#101828'
        },
        '&:focus': {
            borderColor: secondaryColor || '#1649E2'
        }
    };
});
FieldInput.displayName = 'FieldInput';

export { AppInput, FieldInput, ShadCNInput as Input };

